import { inject, Injectable, Signal } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Auth, User as FireUser, authState } from '@angular/fire/auth';
import { GoogleAuthProvider, AuthProvider } from 'firebase/auth';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { User, Role, userConverter } from '../models/user.model';
import { Event } from '../models/event.model';
import { doc, Firestore, docData, setDoc } from '@angular/fire/firestore';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnnouncerModeService } from './announcer-mode.service';

interface Signup {
  displayName: string;
  email: string;
  password: string;
}

export enum LoginErrors {
  INVALID_CREDENTIAL = 'Firebase: Error (auth/invalid-credential).',
  EMAIL_NOT_VALIDATED = 'Email has not been validated.',
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _firestore = inject(Firestore);
  private _router: Router = inject(Router);
  private _afa: AngularFireAuth = inject(AngularFireAuth);
  private _fbAuth: Auth = inject(Auth);
  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _announcerMode: AnnouncerModeService = inject(AnnouncerModeService);
  _user: BehaviorSubject<User | null | undefined> = new BehaviorSubject<
    User | null | undefined
  >(undefined);
  user$: Observable<User | null | undefined> = this._user.asObservable();
  user: Signal<User | null | undefined> = toSignal(this.user$);

  initialize() {
    const authState$ = authState(this._fbAuth).pipe(
      switchMap(async (user: FireUser | null) => {
        const path = globalThis.location.pathname;
        if (user) {
          const userRef = doc(
            this._firestore,
            `users/${user.uid}`,
          ).withConverter(userConverter);

          const userData = docData<User>(userRef, { idField: 'uid' }).pipe(
            switchMap(async (userData) => {
              await this._user.next(userData);

              // Redirect if in the login page.
              if (path == '/login') {
                this._router.navigate(['/events']);
              }
              return userData;
            }),
          );

          // Make sure we read the data from the switchMap
          firstValueFrom(userData);
          return userData;
        } else {
          await this._user.next(null);
          return of(null);
        }
      }),
    );

    // Make sure we read the data from the switchMap
    authState$.subscribe();
    return authState$;
  }

  signupUser(signupUser: Signup): Promise<any> {
    return this._afa
      .createUserWithEmailAndPassword(signupUser.email, signupUser.password)
      .then((result) => {
        this.registerNewUser(result.user, signupUser.displayName);
      })
      .catch((err) => {
        return { isValid: false, message: err.message };
      });
  }

  registerNewUser(fbUser: any, displayName?: string) {
    const user: User = {
      uid: fbUser?.uid,
      displayName: displayName || fbUser.displayName,
      email: fbUser?.email,
      roles: {
        organizer: true,
      },
    };
    this.updateUserData(user);
    fbUser?.sendEmailVerification();
  }

  async loginUser(email: string, password: string): Promise<any> {
    return await this._afa
      .signInWithEmailAndPassword(email, password)
      .catch((err) => {
        return { isValid: false, message: err.message };
      });
  }

  logout(): Promise<void> {
    this._announcerMode.exitAnnouncerMode();
    this._user.next(null);
    return this._afa.signOut();
  }

  private updateUserData(user: User) {
    const userRef = doc(this._firestore, `users/${user.uid}`).withConverter(
      userConverter,
    );
    return setDoc(userRef, userConverter.toFirestore(user));
  }

  ///// Role-based Authorization //////

  isAdmin(): boolean {
    return this.user()?.roles?.admin || false;
  }

  isOrganizer(): boolean {
    return this.user()?.roles?.organizer || false;
  }

  isAnnouncer(): boolean {
    return this.user()?.roles?.announcer || false;
  }

  // Events
  // View - Admin, organizers(partial), Announcer(partial)
  canViewEvents() {
    const allowed = ['admin', 'organizer', 'announcer'];
    return this.checkRoleAuthorization(allowed);
  }
  // Create - Admin, organizers(partial)
  canCreateEvents() {
    const allowed = ['admin', 'organizer'];
    return this.checkRoleAuthorization(allowed);
  }
  // Edit - Admin, organizers(partial)
  canEditEvents(event: Event): boolean {
    const allowed = ['admin', 'organizer'];
    return (
      this.checkRoleAuthorization(allowed) && this.checkEventAssignment(event)
    );
  }
  // Delete - Nobody
  canDeleteEvents() {
    return false;
  }

  // Shoutouts
  // View - Admin, organizers(partial), Announcer(partial)
  canViewShoutouts(event: Event) {
    const allowed = ['admin', 'organizer', 'announcer'];
    return (
      this.checkRoleAuthorization(allowed) && this.checkEventAssignment(event)
    );
  }
  // Create - Open
  canCreateShoutouts() {
    return true;
  }
  // Edit - Nobody
  canEditShoutouts() {
    return false;
  }
  // Delete - Nobody
  canDeleteShoutouts() {
    return false;
  }

  // determines if user has matching role
  public checkRoleAuthorization(allowedRoles: string[]): boolean {
    const user = this.user();
    if (!user) return false;
    if (user.roles.admin) return true;
    for (const role of allowedRoles) {
      if (user.roles[role as keyof Role]) {
        return true;
      }
    }
    return false;
  }

  private checkEventAssignment(event: Event): boolean {
    const user = this.user();
    if (!user) return false;
    if (user.roles.admin) {
      return true;
    }
    if (user.uid && event?.organizerIds?.includes(user.uid)) {
      return true;
    } else if (user.uid && event?.announcerIds?.includes(user.uid)) {
      return true;
    }
    return false;
  }

  ///// OAuth Providers /////

  // Sign in with Google
  GoogleAuth() {
    return this.AuthLogin(new GoogleAuthProvider());
  }

  // Auth logic to run auth providers
  AuthLogin(provider: AuthProvider) {
    return this._afa
      .signInWithPopup(provider)
      .then((result) => {
        if (result.additionalUserInfo?.isNewUser) {
          this.registerNewUser(result.user);
        }
      })
      .catch((err) => {
        return { isValid: false, message: err.message };
      });
  }
}
